/**
 * CoachPlaybook - Sport Selector Component
 * Version: 0.6.0
 * Description: Reusable sport picker with card grid / compact pill modes and age group dropdown
 */

import React from 'react';
import { getAllSports } from '../../sports';

const SportSelector = ({
  selectedSport,
  selectedAgeGroup,
  onSportChange,
  onAgeGroupChange,
  compact = false
}) => {
  const sports = getAllSports();
  const activeSport = sports.find(s => s.id === selectedSport) || null;
  const ageGroups = activeSport?.ageGroups || [];

  // -- Compact mode: horizontal pill bar --
  if (compact) {
    return (
      <div data-element="sport-selector-compact" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Sport pills */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {sports.map(sport => {
            const isActive = sport.id === selectedSport;
            return (
              <button
                key={sport.id}
                data-element={`sport-pill-${sport.id}`}
                onClick={() => onSportChange(sport.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  borderRadius: '9999px',
                  border: `2px solid ${isActive ? sport.accentColor : '#374151'}`,
                  background: isActive ? `${sport.accentColor}22` : '#1f2937',
                  color: isActive ? sport.accentColor : '#9ca3af',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isActive ? 700 : 500,
                  transition: 'all 0.15s ease'
                }}
              >
                <span style={{ fontSize: '16px' }}>{sport.icon}</span>
                <span>{sport.name}</span>
              </button>
            );
          })}
        </div>

        {/* Age group dropdown (inline) */}
        {activeSport && ageGroups.length > 0 && (
          <select
            data-element="sport-age-group-compact"
            value={selectedAgeGroup || ''}
            onChange={(e) => onAgeGroupChange(e.target.value)}
            style={{
              background: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#e5e7eb',
              padding: '6px 10px',
              fontSize: '13px',
              outline: 'none',
              maxWidth: '220px'
            }}
          >
            {ageGroups.map(ag => (
              <option key={ag.id} value={ag.id}>{ag.label}</option>
            ))}
          </select>
        )}
      </div>
    );
  }

  // -- Full mode: card grid --
  return (
    <div data-element="sport-selector-full" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Sport card grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px'
        }}
      >
        {sports.map(sport => {
          const isActive = sport.id === selectedSport;
          return (
            <button
              key={sport.id}
              data-element={`sport-card-${sport.id}`}
              onClick={() => onSportChange(sport.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '20px 12px',
                borderRadius: '16px',
                border: `2px solid ${isActive ? sport.accentColor : '#374151'}`,
                background: isActive ? '#1f2937' : '#111827',
                boxShadow: isActive ? `0 0 12px ${sport.accentColor}33` : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: isActive ? `2px solid ${sport.accentColor}` : 'none',
                outlineOffset: '2px'
              }}
            >
              <span style={{ fontSize: '36px' }}>{sport.icon}</span>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: isActive ? sport.accentColor : '#d1d5db',
                  letterSpacing: '0.01em'
                }}
              >
                {sport.name}
              </span>
              {/* Accent dot indicator */}
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: isActive ? sport.accentColor : '#4b5563',
                  transition: 'background 0.2s ease'
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Age group dropdown */}
      {activeSport && ageGroups.length > 0 && (
        <div
          data-element="sport-age-group-section"
          style={{
            background: '#1f2937',
            borderRadius: '12px',
            border: '1px solid #374151',
            padding: '16px'
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px'
            }}
          >
            Age Group / Level
          </label>
          <select
            data-element="sport-age-group-full"
            value={selectedAgeGroup || ''}
            onChange={(e) => onAgeGroupChange(e.target.value)}
            style={{
              width: '100%',
              background: '#111827',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#e5e7eb',
              padding: '10px 12px',
              fontSize: '14px',
              outline: 'none'
            }}
          >
            {ageGroups.map(ag => (
              <option key={ag.id} value={ag.id}>{ag.label}</option>
            ))}
          </select>
          {/* Show details for selected age group */}
          {selectedAgeGroup && (() => {
            const ag = ageGroups.find(a => a.id === selectedAgeGroup);
            if (!ag) return null;
            return (
              <div
                style={{
                  marginTop: '10px',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                  fontSize: '12px',
                  color: '#9ca3af'
                }}
              >
                {ag.players != null && (
                  <span>{ag.players}v{ag.players}</span>
                )}
                {ag.courtSize && (
                  <span>{ag.courtSize} {activeSport.surfaceType || 'court'}</span>
                )}
                {ag.periods != null && ag.periodLength != null && (
                  <span>{ag.periods} x {ag.periodLength} min</span>
                )}
                {ag.shotClock != null && (
                  <span>Shot clock: {ag.shotClock || 'None'}s</span>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default SportSelector;
